import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";

const EMPTY_BANK = { bank_name: "", account_number: "", account_holder: "" };

export default function Settings() {
  const [form, setForm] = useState({
    company_name: "", address: "", phone: "", fax: "", email: "", tax_id: "",
    logo_base64: "", bank_accounts: [], po_approval_threshold: 0, po_terms_conditions: "",
    signatory_name: "", signatory_title: "Finance Manager", document_prefix: "FIS",
    invoice_prefix: "INV", invoice_start_number: 1, payment_term_days: 30,
    default_ppn_percent: 11, default_include_ppn: true,
  });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/company-profile").then((r) => setForm({ ...r.data, bank_accounts: r.data.bank_accounts || [] }));
  useEffect(() => { load(); }, []);

  const uploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) return toast.error("Logo maks 500KB");
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company-profile", form);
      toast.success("Profil perusahaan disimpan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const addBank = () => setForm({ ...form, bank_accounts: [...form.bank_accounts, { ...EMPTY_BANK }] });
  const updateBank = (i, k, v) => {
    const bs = [...form.bank_accounts];
    bs[i] = { ...bs[i], [k]: v };
    setForm({ ...form, bank_accounts: bs });
  };
  const removeBank = (i) => setForm({ ...form, bank_accounts: form.bank_accounts.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Konfigurasi</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Profil Perusahaan</h1>
        <p className="text-sm text-slate-500 mt-1">Informasi ini muncul di header Invoice PDF, email otomatis, dan dashboard.</p>
      </div>

      <Card className="card-elevated"><CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="flex-shrink-0">
            <Label>Logo Perusahaan</Label>
            <div className="mt-2 w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
              {form.logo_base64
                ? <img src={form.logo_base64} alt="logo" className="max-w-full max-h-full object-contain" data-testid="company-logo-preview" />
                : <Building2 className="text-slate-300" size={40} />}
            </div>
            <label className="mt-2 inline-flex items-center gap-1 text-xs text-navy-900 hover:text-yellow-600 cursor-pointer font-semibold" data-testid="logo-upload-label">
              <Upload size={12} /> {form.logo_base64 ? "Ganti Logo" : "Upload Logo"}
              <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-upload-input" />
            </label>
            {form.logo_base64 && (
              <button
                type="button"
                onClick={() => { setForm({ ...form, logo_base64: "" }); toast.success("Logo dihapus — klik Simpan untuk menyimpan perubahan"); }}
                className="mt-1 inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 cursor-pointer font-semibold ml-3"
                data-testid="logo-remove-btn"
              >
                <Trash2 size={12} /> Hapus Logo
              </button>
            )}
          </div>
          <div className="flex-1 min-w-[280px] grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Nama Perusahaan</Label><Input data-testid="company-name-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
            <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Fax</Label><Input value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} data-testid="company-fax-input" /></div>
            <div><Label>NPWP</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="00.000.000.0-000.000" /></div>
            <div><Label>Prefix Dokumen</Label><Input value={form.document_prefix} onChange={(e) => setForm({ ...form, document_prefix: e.target.value.toUpperCase() })} placeholder="FIS" data-testid="doc-prefix-input" /></div>
            <div className="sm:col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Alamat</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
        </div>
      </CardContent></Card>

      <Card className="card-elevated"><CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-lg font-bold text-slate-900">Rekening Pembayaran</div>
            <p className="text-sm text-slate-500">Muncul di email invoice & PDF sebagai instruksi transfer.</p>
          </div>
          <Button variant="outline" onClick={addBank} data-testid="bank-add-btn"><Plus size={14} className="mr-1" />Tambah Rekening</Button>
        </div>
        <div className="space-y-3">
          {form.bank_accounts.map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-3 rounded-lg">
              <div className="col-span-3"><Label>Bank</Label><Input placeholder="BCA" value={b.bank_name} onChange={(e) => updateBank(i, "bank_name", e.target.value)} data-testid={`bank-name-${i}`} /></div>
              <div className="col-span-4"><Label>Nomor Rekening</Label><Input placeholder="1234567890" className="font-mono" value={b.account_number} onChange={(e) => updateBank(i, "account_number", e.target.value)} data-testid={`bank-account-${i}`} /></div>
              <div className="col-span-4"><Label>Atas Nama</Label><Input placeholder="PT Nusantara..." value={b.account_holder} onChange={(e) => updateBank(i, "account_holder", e.target.value)} data-testid={`bank-holder-${i}`} /></div>
              <div className="col-span-1"><Button size="sm" variant="ghost" onClick={() => removeBank(i)}><Trash2 size={14} className="text-red-500" /></Button></div>
            </div>
          ))}
          {form.bank_accounts.length === 0 && <div className="text-center text-sm text-slate-400 py-6">Belum ada rekening. Tambahkan agar customer tahu ke mana harus transfer.</div>}
        </div>
      </CardContent></Card>

      <Card className="card-elevated"><CardContent className="p-6">
        <div className="mb-4">
          <div className="font-display text-lg font-bold text-slate-900">Approval Workflow</div>
          <p className="text-sm text-slate-500">PO dengan total ≥ threshold ini akan otomatis masuk antrean approval Owner sebelum dapat diterima.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Threshold Approval PO (Rp)</Label>
            <Input type="number" min="0" value={form.po_approval_threshold} onChange={(e) => setForm({ ...form, po_approval_threshold: Number(e.target.value) })} placeholder="0 = matikan" data-testid="threshold-input" />
            <div className="text-[11px] text-slate-500 mt-1">Set 0 untuk menonaktifkan approval workflow (semua PO auto approved)</div>
          </div>
          <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="font-semibold mb-1">Preview aturan aktif</div>
            {Number(form.po_approval_threshold) > 0
              ? <div className="text-slate-600">PO ≥ <strong className="text-navy-900">Rp {Number(form.po_approval_threshold).toLocaleString("id-ID")}</strong> butuh approval Owner</div>
              : <div className="text-slate-500 italic">Approval workflow tidak aktif</div>}
          </div>
        </div>
      </CardContent></Card>

      <Card className="card-elevated"><CardContent className="p-6">
        <div className="mb-4">
          <div className="font-display text-lg font-bold text-slate-900">Syarat & Ketentuan PO</div>
          <p className="text-sm text-slate-500">Teks ini akan tercetak di setiap Purchase Order (PDF) sebagai perjanjian dengan supplier. Gunakan penomoran (1., 2., 3., ...) agar rapi.</p>
        </div>
        <Textarea
          rows={9}
          className="font-mono text-sm"
          value={form.po_terms_conditions}
          onChange={(e) => setForm({ ...form, po_terms_conditions: e.target.value })}
          placeholder="1. Pembayaran dalam 30 hari...&#10;2. Barang tidak sesuai dapat dikembalikan..."
          data-testid="po-terms-input"
        />
        <div className="text-[11px] text-slate-500 mt-2">Kosongkan untuk menonaktifkan bagian S&K di PO PDF.</div>
      </CardContent></Card>

      <div className="flex justify-end">
        <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} disabled={saving} data-testid="company-save-btn">{saving ? "Menyimpan..." : "Simpan Perubahan"}</Button>
      </div>
    </div>
  );
}
